/**
 * documentsClient — wraps the typed `apiClient.retail.documents` surface with the
 * graceful-fallback behavior the RDL module needs:
 *
 *   - When `/v3/documents/list` returns 503 (`SP_UNAVAILABLE`) or the call
 *     fails outright, fall back to the bundled fixture so the module is
 *     reviewable without requiring SharePoint wiring.
 *   - When `/v3/documents/types` 501s, fall back to the locally-derived
 *     DocumentType catalog (matches DocumentRequirements docCodes used in
 *     RAO).
 *   - When `/v3/documents/upload` and `/v3/documents/{id}/content` 501, the
 *     callers handle the failure with friendly UX (no inline preview, "queued
 *     for upload" banner).
 *
 * The single seam here means the rest of the module is shaped like real-data
 * code from day one — when the Function side ships the missing routes,
 * removing the fixture is a one-file change.
 */

import type { ApiClient } from '@retail-ops/api-client';
import type {
  DocumentRef,
  DocumentSearchFilters,
  DocumentType,
  DocumentUploadRequest,
} from '@retail-ops/api-client';

// ---------------------------------------------------------------------------
// Shared fixture — re-exported here so the hooks share one source of truth.
// Mirrors the legacy useDocuments fixture (12 docs across 3 CIFs / 4 carts).
// ---------------------------------------------------------------------------

export const FIXTURE_DOCUMENTS: ReadonlyArray<DocumentRef> = [
  {
    documentId: 'doc_00001',
    fileName: 'driver-license-front.jpg',
    sizeBytes: 412_880,
    contentType: 'image/jpeg',
    category: 'fsr_upload',
    auroraStatus: 'indexed',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'ssmith@mypcb.com',
    uploadedAt: '2026-04-21T14:15:10Z',
    relativePath: '0001234/cart-f7a91c/cip/driver-license-front.jpg',
    previewable: true,
    docCode: 'DL_FRONT',
  },
  {
    documentId: 'doc_00002',
    fileName: 'driver-license-back.jpg',
    sizeBytes: 389_114,
    contentType: 'image/jpeg',
    category: 'fsr_upload',
    auroraStatus: 'indexed',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'ssmith@mypcb.com',
    uploadedAt: '2026-04-21T14:15:22Z',
    relativePath: '0001234/cart-f7a91c/cip/driver-license-back.jpg',
    previewable: true,
    docCode: 'DL_BACK',
  },
  {
    documentId: 'doc_00003',
    fileName: 'disclosure-package-signed.pdf',
    sizeBytes: 2_714_336,
    contentType: 'application/pdf',
    category: 'signed_pdf',
    auroraStatus: 'exported',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'signnow-webhook',
    uploadedAt: '2026-04-21T17:42:05Z',
    relativePath: '0001234/cart-f7a91c/signed/disclosure-package-signed.pdf',
    previewable: true,
    docCode: 'DISC_PKG',
  },
  {
    documentId: 'doc_00004',
    fileName: 'deposit-agreement-v14.pdf',
    sizeBytes: 187_204,
    contentType: 'application/pdf',
    category: 'rao_output',
    auroraStatus: 'indexed',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'rao-pipeline',
    uploadedAt: '2026-04-21T15:03:44Z',
    relativePath: '0001234/cart-f7a91c/rendered/deposit-agreement-v14.pdf',
    previewable: true,
    docCode: 'DEPOSIT_AGMT',
  },
  {
    documentId: 'doc_00005',
    fileName: 'signature-card.pdf',
    sizeBytes: 95_442,
    contentType: 'application/pdf',
    category: 'rao_output',
    auroraStatus: 'indexed',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'rao-pipeline',
    uploadedAt: '2026-04-21T15:03:46Z',
    relativePath: '0001234/cart-f7a91c/rendered/signature-card.pdf',
    previewable: true,
    docCode: 'SIG_CARD',
  },
  {
    documentId: 'doc_00006',
    fileName: 'utility-bill-proof-of-address.pdf',
    sizeBytes: 512_910,
    contentType: 'application/pdf',
    category: 'customer_upload',
    auroraStatus: 'indexed',
    cifId: '0001234',
    uploadedBy: 'customer-portal',
    uploadedAt: '2026-04-20T22:11:02Z',
    relativePath: '0001234/utility-bill-proof-of-address.pdf',
    previewable: true,
    docCode: 'PROOF_ADDR',
  },
  {
    documentId: 'doc_00007',
    fileName: 'trust-certification.pdf',
    sizeBytes: 1_108_774,
    contentType: 'application/pdf',
    category: 'customer_upload',
    auroraStatus: 'indexed',
    cifId: '0002997',
    cartId: 'cart_21bc4d',
    uploadedBy: 'customer-portal',
    uploadedAt: '2026-04-19T19:00:55Z',
    relativePath: '0002997/cart-21bc4d/trust-certification.pdf',
    previewable: true,
    docCode: 'TRUST_CERT',
  },
  {
    documentId: 'doc_00008',
    fileName: 'business-resolution.pdf',
    sizeBytes: 624_580,
    contentType: 'application/pdf',
    category: 'fsr_upload',
    auroraStatus: 'indexed',
    cifId: '0002997',
    cartId: 'cart_21bc4d',
    uploadedBy: 'bwiggins@mypcb.com',
    uploadedAt: '2026-04-19T19:14:11Z',
    relativePath: '0002997/cart-21bc4d/cip/business-resolution.pdf',
    previewable: true,
    docCode: 'BIZ_RES',
  },
  {
    documentId: 'doc_00009',
    fileName: 'w9-signed.pdf',
    sizeBytes: 220_002,
    contentType: 'application/pdf',
    category: 'signed_pdf',
    auroraStatus: 'pending',
    cifId: '0002997',
    cartId: 'cart_21bc4d',
    uploadedBy: 'signnow-webhook',
    uploadedAt: '2026-04-19T21:42:33Z',
    relativePath: '0002997/cart-21bc4d/signed/w9-signed.pdf',
    previewable: true,
    docCode: 'W9',
  },
  {
    documentId: 'doc_00010',
    fileName: 'adverse-action-notice.pdf',
    sizeBytes: 98_112,
    contentType: 'application/pdf',
    category: 'bank_generated',
    auroraStatus: 'exported',
    cifId: '0004512',
    uploadedBy: 'rcc-adverse-action',
    uploadedAt: '2026-04-18T13:22:00Z',
    relativePath: '0004512/compliance/adverse-action-notice.pdf',
    previewable: true,
    docCode: 'ADV_ACTION',
  },
  {
    documentId: 'doc_00011',
    fileName: 'passport-scan.jpg',
    sizeBytes: 718_990,
    contentType: 'image/jpeg',
    category: 'customer_upload',
    auroraStatus: 'failed',
    cifId: '0004512',
    uploadedBy: 'customer-portal',
    uploadedAt: '2026-04-18T12:00:14Z',
    relativePath: '0004512/passport-scan.jpg',
    previewable: true,
    docCode: 'PASSPORT',
  },
  {
    documentId: 'doc_00012',
    fileName: 'beneficiary-designation-ira.pdf',
    sizeBytes: 155_408,
    contentType: 'application/pdf',
    category: 'signed_pdf',
    auroraStatus: 'indexed',
    cifId: '0004512',
    cartId: 'cart_9c31ef',
    uploadedBy: 'signnow-webhook',
    uploadedAt: '2026-04-18T14:55:09Z',
    relativePath: '0004512/cart-9c31ef/signed/beneficiary-designation-ira.pdf',
    previewable: true,
    docCode: 'IRA_BENE',
  },
];

/** Local DocumentType catalog used when the server returns 501. */
export const FIXTURE_DOCUMENT_TYPES: ReadonlyArray<DocumentType> = [
  { docCode: 'DL_FRONT', label: 'Driver License — Front', fulfillsRequirement: true, allowedExtensions: ['jpg', 'jpeg', 'png', 'pdf'], maxSizeMb: 8 },
  { docCode: 'DL_BACK', label: 'Driver License — Back', fulfillsRequirement: true, allowedExtensions: ['jpg', 'jpeg', 'png', 'pdf'], maxSizeMb: 8 },
  { docCode: 'PASSPORT', label: 'Passport', fulfillsRequirement: true, allowedExtensions: ['jpg', 'jpeg', 'png', 'pdf'], maxSizeMb: 8 },
  { docCode: 'STATE_ID', label: 'State ID Card', fulfillsRequirement: true, allowedExtensions: ['jpg', 'jpeg', 'png', 'pdf'], maxSizeMb: 8 },
  { docCode: 'PROOF_ADDR', label: 'Proof of Address', fulfillsRequirement: true, allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png'], maxSizeMb: 8 },
  { docCode: 'SSN_CARD', label: 'Social Security Card', fulfillsRequirement: false, allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png'], maxSizeMb: 8 },
  { docCode: 'W9', label: 'W-9 (signed)', fulfillsRequirement: true, allowedExtensions: ['pdf'], maxSizeMb: 4 },
  { docCode: 'TRUST_CERT', label: 'Trust Certification', fulfillsRequirement: true, allowedExtensions: ['pdf'], maxSizeMb: 8 },
  { docCode: 'BIZ_RES', label: 'Business Resolution', fulfillsRequirement: true, allowedExtensions: ['pdf'], maxSizeMb: 8 },
  { docCode: 'EIN_LETTER', label: 'EIN Letter (IRS CP-575)', fulfillsRequirement: true, allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png'], maxSizeMb: 4 },
  { docCode: 'IRA_BENE', label: 'IRA Beneficiary Designation', fulfillsRequirement: true, allowedExtensions: ['pdf'], maxSizeMb: 4 },
  { docCode: 'OTHER', label: 'Other (free-form)', fulfillsRequirement: false, allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png', 'tiff', 'tif', 'heic'], maxSizeMb: 16 },
];

// ---------------------------------------------------------------------------
// Documents client wrapper
// ---------------------------------------------------------------------------

export interface DocumentsListOptions {
  cifId?: string;
  cartId?: string;
  signal?: AbortSignal;
}

export interface DocumentsListResult {
  documents: ReadonlyArray<DocumentRef>;
  /** True when the result came from the bundled fixture rather than the live route. */
  isFixture: boolean;
  /** Friendly-language reason banner copy when fallback is used. */
  fixtureNotice: string | null;
}

const FIXTURE_NOTICE =
  'Showing demo documents. The SharePoint document library is still being wired up — uploads, deletes, and live previews will activate automatically once it goes online.';

function applyClientFilters(
  rows: ReadonlyArray<DocumentRef>,
  opts: { cifId?: string; cartId?: string },
): DocumentRef[] {
  return rows.filter((d) => {
    if (opts.cifId && d.cifId !== opts.cifId) return false;
    if (opts.cartId && d.cartId !== opts.cartId) return false;
    return true;
  });
}

function searchFixture(filters: DocumentSearchFilters): DocumentRef[] {
  const needle = (filters.q ?? '').trim().toLowerCase();
  return FIXTURE_DOCUMENTS.filter((d) => {
    if (filters.cifId && d.cifId !== filters.cifId) return false;
    if (filters.category && d.category !== filters.category) return false;
    if (filters.docCode && d.docCode !== filters.docCode) return false;
    if (filters.from && d.uploadedAt < filters.from) return false;
    if (filters.to && d.uploadedAt > filters.to) return false;
    if (needle.length > 0) {
      const hay =
        `${d.fileName} ${d.cifId} ${d.cartId ?? ''} ${d.uploadedBy} ${d.docCode ?? ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
}

/**
 * PILOT 2026-09-04 — fixtures are a DEV convenience and must never stand in for
 * a customer's real document set in front of an FSR.
 *
 * Before this guard, `list()` and `search()` fell back to FIXTURE_DOCUMENTS on
 * ANY failure — including a transient network blip — so a momentary outage
 * showed twelve fabricated documents belonging to invented CIFs. There is a
 * "Demo data" banner, but an info-toned banner above plausible-looking customer
 * records is not an adequate control in a bank: the safe failure is NO
 * documents plus an error, never INVENTED documents.
 *
 * In production the fallback is refused and the failure propagates, so the
 * existing `error` state in useDocumentsByCif / useDocumentsByCart surfaces it.
 * In dev the fixtures behave exactly as before.
 */
const FIXTURES_ALLOWED = import.meta.env.DEV;

class DocumentsUnavailableError extends Error {
  public constructor() {
    super('Documents could not be loaded from the server. Nothing is shown rather than risk showing the wrong customer’s documents — try again, and tell an administrator if it persists.');
    this.name = 'DocumentsUnavailableError';
  }
}

export class DocumentsClient {
  public constructor(private readonly api: ApiClient | null) {}

  /** List documents — returns the fixture when the route is unavailable. */
  public async list(opts: DocumentsListOptions = {}): Promise<DocumentsListResult> {
    if (!this.api) {
      if (!FIXTURES_ALLOWED) throw new DocumentsUnavailableError();
      const docs = applyClientFilters(FIXTURE_DOCUMENTS, opts);
      return { documents: docs, isFixture: true, fixtureNotice: FIXTURE_NOTICE };
    }
    try {
      const resp = await this.api.retail.documents.list(
        { ...(opts.cifId ? { cifId: opts.cifId } : {}), ...(opts.cartId ? { cartId: opts.cartId } : {}) },
        opts.signal ? { signal: opts.signal } : undefined,
      );
      if (resp?.success && Array.isArray(resp.data)) {
        return { documents: resp.data, isFixture: false, fixtureNotice: null };
      }
      if (!FIXTURES_ALLOWED) throw new DocumentsUnavailableError();
      const docs = applyClientFilters(FIXTURE_DOCUMENTS, opts);
      return { documents: docs, isFixture: true, fixtureNotice: FIXTURE_NOTICE };
    } catch (err) {
      if (!FIXTURES_ALLOWED) {
        throw err instanceof DocumentsUnavailableError ? err : new DocumentsUnavailableError();
      }
      const docs = applyClientFilters(FIXTURE_DOCUMENTS, opts);
      return { documents: docs, isFixture: true, fixtureNotice: FIXTURE_NOTICE };
    }
  }

  /** Search across the library. Falls back to the fixture set. */
  public async search(filters: DocumentSearchFilters, signal?: AbortSignal): Promise<DocumentsListResult> {
    if (!this.api) {
      if (!FIXTURES_ALLOWED) throw new DocumentsUnavailableError();
      return {
        documents: searchFixture(filters),
        isFixture: true,
        fixtureNotice: FIXTURE_NOTICE,
      };
    }
    try {
      const resp = await this.api.retail.documents.search(
        filters,
        signal ? { signal } : undefined,
      );
      if (resp?.success && Array.isArray(resp.data)) {
        return { documents: resp.data, isFixture: false, fixtureNotice: null };
      }
      if (!FIXTURES_ALLOWED) throw new DocumentsUnavailableError();
      return {
        documents: searchFixture(filters),
        isFixture: true,
        fixtureNotice: FIXTURE_NOTICE,
      };
    } catch (err) {
      if (!FIXTURES_ALLOWED) {
        throw err instanceof DocumentsUnavailableError ? err : new DocumentsUnavailableError();
      }
      return {
        documents: searchFixture(filters),
        isFixture: true,
        fixtureNotice: FIXTURE_NOTICE,
      };
    }
  }

  /** Resolve the DocumentType catalog (server preferred, fixture fallback). */
  public async types(signal?: AbortSignal): Promise<ReadonlyArray<DocumentType>> {
    if (!this.api) return FIXTURE_DOCUMENT_TYPES;
    try {
      const resp = await this.api.retail.documents.types(signal ? { signal } : undefined);
      if (resp?.success && Array.isArray(resp.data) && resp.data.length > 0) {
        return resp.data;
      }
      return FIXTURE_DOCUMENT_TYPES;
    } catch {
      return FIXTURE_DOCUMENT_TYPES;
    }
  }

  /** Upload a document. */
  public async upload(req: DocumentUploadRequest, signal?: AbortSignal): Promise<{ success: boolean; documentId?: string; error?: string }> {
    if (!this.api) {
      return { success: false, error: 'Document service is offline.' };
    }
    try {
      const resp = await this.api.retail.documents.upload(req, signal ? { signal } : undefined);
      const result: { success: boolean; documentId?: string; error?: string } = { success: !!resp?.success };
      if (resp?.documentId) result.documentId = resp.documentId;
      if (resp?.error) result.error = resp.error;
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed.';
      return { success: false, error: msg };
    }
  }

  /** Soft-delete a document (Manager+ on the server). */
  public async remove(documentId: string, reason?: string, signal?: AbortSignal): Promise<{ success: boolean; error?: string }> {
    if (!this.api) {
      return { success: false, error: 'Document service is offline.' };
    }
    try {
      const resp = await this.api.retail.documents.remove(documentId, reason, signal ? { signal } : undefined);
      const result: { success: boolean; error?: string } = { success: !!resp?.success };
      if (resp?.error) result.error = resp.error;
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed.';
      return { success: false, error: msg };
    }
  }

  /**
   * Resolve a preview URL for the given document. Returns either:
   *   - { kind: 'inline-bytes', dataUrl } — small files (base64 inline)
   *   - { kind: 'signed-url', url } — large files
   *   - { kind: 'unavailable', reason } — server route not live (fall back to the per-tile icon).
   */
  public async previewUrl(
    documentId: string,
    signal?: AbortSignal,
  ): Promise<
    | { kind: 'inline-bytes'; dataUrl: string; contentType: string }
    | { kind: 'signed-url'; url: string; contentType: string; blob?: Blob }
    | { kind: 'unavailable'; reason: string }
  > {
    if (!this.api) {
      return { kind: 'unavailable', reason: 'Document streaming is offline.' };
    }
    try {
      const resp = await this.api.retail.documents.content(documentId, signal ? { signal } : undefined);
      if (resp.bytesBase64) {
        return {
          kind: 'inline-bytes',
          dataUrl: `data:${resp.contentType};base64,${resp.bytesBase64}`,
          contentType: resp.contentType,
        };
      }
      if (resp.signedUrl) {
        // Over the inline ceiling. `signedUrl` is a path back to our own
        // Function, NOT a storage URL, so it must be fetched WITH auth headers —
        // handing it straight to an <iframe> would arrive unauthenticated and
        // 401. Pull the bytes as an authenticated Blob and hand the UI a
        // blob: URL instead. The caller revokes it on unmount.
        const blob = await this.api.retail.documents.contentBlob(
          documentId,
          signal ? { signal } : undefined,
        );
        return {
          kind: 'signed-url',
          url: URL.createObjectURL(blob),
          contentType: blob.type || resp.contentType,
          // The bytes too: pdf.js can't fetch a blob: URL under the app's CSP (CHG-0097).
          blob,
        };
      }
      return { kind: 'unavailable', reason: 'Document content is not available right now.' };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Preview unavailable.';
      return { kind: 'unavailable', reason: msg };
    }
  }
}
